import { useNavigate } from 'react-router-dom'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { PROCUREMENT_STATE_LABELS } from '@crm/shared'
import type { RequestItemProcurementState } from '@crm/shared'
import type { RosDashboardFilters, RosFunnelRow } from './types'
import { useCanOpenQueue } from '../../procurement/queueAccess'
import { queueLink } from './useRosDashboard'

// Воронка процесса закупщика: пончик по per-user/командным статусам + легенда.
// Каждый сегмент/строка легенды кликабельны → очередь с procState. У ADMIN
// очередь закрыта (18.09) — у него без кликов (решение Сергея 25.09).

const LEGEND_ROW = 'w-full flex items-center justify-between gap-2 text-sm px-2 py-1 rounded-lg'

const STATE_COLORS: Record<RequestItemProcurementState, string> = {
  NEW: '#94a3b8',
  REQUESTED: '#3b82f6',
  WAITING: '#f59e0b',
  NEEDS_INFO: '#8b5cf6',
  QUOTED: '#10b981',
  NOT_FOUND: '#f97316',
  NO_RESPONSE: '#ef4444',
  REFUSED: '#dc2626',
}

export function RosFunnelDonut({
  funnel,
  filters,
}: {
  funnel: RosFunnelRow[]
  filters: RosDashboardFilters
}) {
  const navigate = useNavigate()
  const canQueue = useCanOpenQueue()
  const total = funnel.reduce((a, r) => a + r.count, 0)
  const data = funnel.filter((r) => r.count > 0)
  const goTo = (state: RequestItemProcurementState) =>
    navigate(queueLink({ procState: state }, filters))

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 h-full flex flex-col">
      <div className="text-sm text-gray-500 mb-2">Воронка закупки · статусы закупщиков</div>
      {total === 0 ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
          Нет позиций в очереди
        </div>
      ) : (
        <div className="flex-1 flex flex-col sm:flex-row items-center gap-4">
          <div className="w-full sm:w-1/2 min-h-[200px] h-[200px]">
            <ResponsiveContainer width="100%" height="100%" minHeight={120}>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="count"
                  nameKey="state"
                  innerRadius="55%"
                  outerRadius="85%"
                  paddingAngle={2}
                  onClick={
                    canQueue
                      ? (seg) => {
                          const st = (seg as { state?: RequestItemProcurementState })?.state
                          if (st) goTo(st)
                        }
                      : undefined
                  }
                  cursor={canQueue ? 'pointer' : undefined}
                >
                  {data.map((r) => (
                    <Cell key={r.state} fill={STATE_COLORS[r.state]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v, _n, p) => {
                    const st = (p as { payload?: RosFunnelRow })?.payload?.state
                    return [`${Number(v)} поз.`, st ? PROCUREMENT_STATE_LABELS[st] : '']
                  }}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="w-full sm:w-1/2 space-y-1">
            {funnel.map((r) => {
              const row = (
                <>
                  <span className="flex items-center gap-2 text-gray-700">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: STATE_COLORS[r.state] }}
                    />
                    {PROCUREMENT_STATE_LABELS[r.state]}
                  </span>
                  <span className="font-semibold text-gray-900 tabular-nums">
                    {r.count.toLocaleString('ru-RU')}
                  </span>
                </>
              )
              if (!canQueue)
                return (
                  <div key={r.state} className={LEGEND_ROW}>
                    {row}
                  </div>
                )
              return (
                <button
                  key={r.state}
                  onClick={() => goTo(r.state)}
                  className={`${LEGEND_ROW} hover:bg-gray-50 transition-colors`}
                  title="Открыть очередь с этим статусом"
                >
                  {row}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
